import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { calculateChainLink, getFabricDesc, getRailDiameter } from '../utils/chainLinkCalc';
import { round2 } from '../utils/lineMath';
import { getPackSize, roundToPackage } from '../utils/packageRounding';

function EstimatorResults({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const specs = location.state;
  const { products } = useData();

  const [materials, setMaterials] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [calculated, setCalculated] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [extraSearch, setExtraSearch] = useState('');
  const [showExtraDropdown, setShowExtraDropdown] = useState(false);

  const userName = user?.email?.split('@')[0]?.toUpperCase();

  useEffect(() => {
    if (specs && products.length > 0 && !calculated) {
      const { items, warnings: warns } = calculateChainLink(specs, products, 'A');
      setMaterials(items);
      setWarnings(warns);
      setCalculated(true);
    }
  }, [specs, products, calculated]);

  if (!specs) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#CDE7F5' }}>
        <div className="text-center">
          <p style={{ color: '#6B7C93', fontSize: 20, marginBottom: 16 }}>No estimate data found.</p>
          <button onClick={() => navigate('/estimator')} style={{ background: '#0077B6', border: 'none', color: '#fff', padding: '12px 24px', borderRadius: 12, fontWeight: 700, cursor: 'pointer' }}>
            ← Back to Estimator
          </button>
        </div>
      </div>
    );
  }

  // ── Derived summary stats ──
  const totalFootage = (specs.runs || []).reduce((s, r) => s + (parseFloat(r.length) || 0), 0);
  const spacing = parseFloat(specs.postSpacing) || 10;
  const linePosts = (specs.runs || []).reduce((sum, r) => {
    const ft = parseFloat(r.length) || 0;
    return ft > 0 ? sum + Math.max(0, Math.floor(ft / spacing) - 1) : sum;
  }, 0);
  const swingGates = (specs.gates || []).filter(g => g.type !== 'Cantilever');
  const cantGates = (specs.gates || []).filter(g => g.type === 'Cantilever');

  const fabricDisplay = getFabricDesc(specs.finish, specs.fenceClass, specs.height);
  const railDiam = getRailDiameter(specs.fenceClass, specs.height);

  // ── Item edit helpers ──
  const updateQty = (id, qty) => setMaterials(m => m.map(i => i.id === id ? { ...i, qty: parseFloat(qty) || 0 } : i));
  const updatePrice = (id, price) => setMaterials(m => m.map(i => i.id === id ? { ...i, unitPrice: parseFloat(price) || 0 } : i));
  const removeItem = (id) => setMaterials(m => m.filter(i => i.id !== id));
  // Round ONE row up to a whole package, using that product's own packQty.
  //
  // This replaced a pair of fixed ↑10 / ↑25 buttons. Those rounded to factors no
  // real package uses — they could not reach a 1320' roll of barbed wire, a
  // 625-piece bag of hog rings or a 420-piece bag of the black ones — on the
  // one screen where the salesperson judges a Materials Subtotal BEFORE the
  // items are imported. Offering a control that cannot produce a sellable
  // quantity is worse than offering none.
  const roundRowToPackage = (item) => {
    const packSize = getPackSize(item, products);
    if (!packSize) return;
    setMaterials(m => m.map(i => i.id === item.id ? { ...i, qty: roundToPackage(i.qty, packSize) } : i));
  };

  const addExtraProduct = (product) => {
    setMaterials(m => [...m, {
      id: Date.now() + Math.random(),
      sku: product.sku,
      description: product.description,
      qty: 1,
      unit: product.unit || 'pc',
      unitPrice: parseFloat(product.priceA || 0),
      isStock: product.isStock,
      productId: product.id,
      found: true,
      roundable: false,
      fromEstimator: true,
    }]);
    setExtraSearch('');
    setShowExtraDropdown(false);
  };

  const filteredExtra = products.filter(p =>
    p.isActive && extraSearch && (
      p.description?.toLowerCase().includes(extraSearch.toLowerCase()) ||
      p.sku?.toLowerCase().includes(extraSearch.toLowerCase())
    )
  );

  // Sum of the ROUNDED per-row totals — the same 2-decimal figures shown in the
  // Total column below — so the rows always add up to this subtotal on screen.
  const subtotal = round2(materials.reduce((s, i) => s + round2(parseFloat(i.qty) * parseFloat(i.unitPrice)), 0));

  const summaryTags = [
    { label: specs.fenceClass === 'Residential' ? 'Light (Residential)' : 'Heavy (Commercial)', color: 'bg-blue-100 text-blue-700' },
    { label: `${specs.height === '3.5' ? "3'-6\"" : `${specs.height}'`} Height`, color: 'bg-blue-100 text-blue-700' },
    { label: specs.finish, color: 'bg-blue-100 text-blue-700' },
    { label: `${totalFootage.toLocaleString()} ft`, color: 'bg-gray-100 text-gray-700' },
    specs.topRail && { label: `Top Rail (${railDiam})`, color: 'bg-green-100 text-green-700' },
    specs.bottomRail && { label: `Bottom Rail (${railDiam})`, color: 'bg-green-100 text-green-700' },
    specs.midRail && { label: `Mid Rail (${railDiam})`, color: 'bg-green-100 text-green-700' },
    specs.braceRail && { label: `Brace Rail (${railDiam})`, color: 'bg-green-100 text-green-700' },
    specs.barbWire && { label: 'Barb Wire', color: 'bg-yellow-100 text-yellow-700' },
    specs.coilTop && { label: 'Coil Wire Top', color: 'bg-yellow-100 text-yellow-700' },
    specs.coilBottom && { label: 'Coil Wire Bottom', color: 'bg-yellow-100 text-yellow-700' },
    (specs.gates || []).length > 0 && { label: `${specs.gates.length} Gate(s)`, color: 'bg-purple-100 text-purple-700' },
  ].filter(Boolean);

  return (
    <div className="min-h-screen" style={{ background: '#CDE7F5', padding: 20 }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Header card — flat NewDocument-style */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, flexShrink: 0 }}>
              <img src={require('../assets/fenceflow-logo.png')} alt="Fence Flow" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#03045E' }}>Estimator Results</h1>
              {userName && (
                <p style={{ color: '#6B7C93', fontSize: 12, margin: '4px 0 0 0', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>
                  Salesperson: <span style={{ color: '#0077B6' }}>{userName}</span>
                </p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/estimator', { state: specs })}
              style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              ← Edit Estimate
            </button>
          </div>
        </div>


        {/* Summary */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16 }}>
          <h2 style={{ color: '#03045E', fontSize: 18, fontWeight: 700, margin: '0 0 12px 0' }}>Estimate Summary</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {summaryTags.map((t, i) => (
              <span key={i} style={{ background: '#E6F1FB', color: '#0077B6', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999 }}>{t.label}</span>
            ))}
          </div>
          <div style={{ color: '#6B7C93', fontSize: 12, marginBottom: 8, fontWeight: 500 }}>Fabric: <span style={{ color: '#03045E', fontWeight: 700 }}>{fabricDisplay}</span></div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Total Footage', val: `${totalFootage.toLocaleString()} ft`, color: '#0077B6' },
              { label: 'Line Posts', val: linePosts, color: '#0077B6' },
              { label: 'End Posts', val: specs.totalEnds || 0, color: '#0077B6' },
              { label: 'Corner Posts', val: specs.totalCorners || 0, color: '#0077B6' },
              { label: 'Gate Posts', val: swingGates.length * 2 + cantGates.length * 3, color: '#0F6E56' },
            ].map(item => (
              <div key={item.label} style={{ background: '#CDE7F5', border: '1px solid #E3ECF5', borderRadius: 10, padding: 8, textAlign: 'center' }}>
                <p style={{ color: '#6B7C93', fontSize: 12, margin: 0 }}>{item.label}</p>
                <p style={{ color: item.color, fontWeight: 700, fontSize: 18, margin: 0 }}>{item.val}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div style={{ background: '#FDF1DD', border: '1px solid #EBD9B4', borderRadius: 16, padding: 16, marginBottom: 16 }}>
            <p style={{ color: '#854F0B', fontWeight: 700, margin: '0 0 8px 0' }}>⚠ {warnings.length} item{warnings.length !== 1 ? 's' : ''} not found in price list</p>
            <p style={{ color: '#9A6B2E', fontSize: 14, margin: '0 0 8px 0' }}>Add these SKUs to your price list to show pricing:</p>
            <div className="space-y-1">
              {warnings.map((w, i) => <p key={i} style={{ color: '#9A6B2E', fontSize: 14, fontFamily: 'monospace', margin: 0 }}>• {w}</p>)}
            </div>
          </div>
        )}

        {/* Controls */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 16, marginBottom: 16 }}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <button onClick={() => setEditMode(!editMode)}
              style={{ padding: '8px 16px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: `1px solid ${editMode ? '#854F0B' : '#E3ECF5'}`,
                background: editMode ? '#FDF1DD' : '#FFFFFF', color: editMode ? '#854F0B' : '#03045E' }}>
              {editMode ? '✏ Editing — Click to lock' : '✏ Edit Quantities & Prices'}
            </button>
            <span style={{ color: '#6B7C93', fontSize: 12 }}>{editMode ? 'Tap ✕ to remove items · edit qty/price directly' : 'Turn on to modify the estimate'}</span>
          </div>
          <div className="relative">
            <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>+ Add Extra Product</label>
            <input type="text" placeholder="Search by description or SKU…"
              value={extraSearch}
              onChange={e => { setExtraSearch(e.target.value); setShowExtraDropdown(true); }}
              onFocus={e => { setShowExtraDropdown(true); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
              onBlur={e => { setTimeout(() => setShowExtraDropdown(false), 150); e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
              style={{ width: '100%', border: '1px solid #E3ECF5', borderRadius: 10, padding: '10px 14px', fontSize: 14, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }} />
            {showExtraDropdown && filteredExtra.length > 0 && (
              <div style={{ position: 'absolute', zIndex: 10, width: '100%', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 12, boxShadow: '0 12px 30px rgba(3,4,94,0.12)', marginTop: 4, maxHeight: 192, overflowY: 'auto' }}>
                {filteredExtra.slice(0, 30).map(p => (
                  <div key={p.id} onMouseDown={() => addExtraProduct(p)}
                    style={{ padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid #E3ECF5' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#E6F1FB'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <p style={{ fontWeight: 700, fontSize: 14, color: '#03045E', margin: 0 }}>{p.description}</p>
                    <p style={{ color: '#6B7C93', fontSize: 12, margin: 0 }}>SKU: <span style={{ color: '#0077B6', fontFamily: 'monospace' }}>{p.sku}</span> · ${parseFloat(p.priceA || 0).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Materials Table */}
        {!calculated && products.length === 0 ? (
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 32, textAlign: 'center', color: '#6B7C93', marginBottom: 16 }}>Loading products…</div>
        ) : (
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, overflow: 'hidden', marginBottom: 16 }}>
            <table className="w-full text-sm">
              <thead style={{ background: '#E6F1FB' }}>
                <tr>
                  <th className="px-3 py-3 text-left w-24" style={{ color: '#6B7C93', fontWeight: 600 }}>SKU</th>
                  <th className="px-3 py-3 text-left" style={{ color: '#6B7C93', fontWeight: 600 }}>Description</th>
                  <th className="px-3 py-3 text-center w-20" style={{ color: '#6B7C93', fontWeight: 600 }}>Qty</th>
                  <th className="px-3 py-3 text-center w-14" style={{ color: '#6B7C93', fontWeight: 600 }}>Unit</th>
                  <th className="px-3 py-3 text-right w-24" style={{ color: '#6B7C93', fontWeight: 600 }}>Unit Price</th>
                  <th className="px-3 py-3 text-right w-24" style={{ color: '#6B7C93', fontWeight: 600 }}>Total</th>
                  {editMode && <th className="px-3 py-3 text-center w-28" style={{ color: '#6B7C93', fontWeight: 600 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {materials.map((item, idx) => (
                  <tr key={item.id}
                    style={{ background: idx % 2 === 0 ? '#FFFFFF' : '#CDE7F5', borderTop: '1px solid #E3ECF5', borderLeft: !item.found ? '4px solid #854F0B' : 'none' }}>
                    <td className="px-3 py-2 font-mono text-xs font-bold" style={{ color: '#0077B6' }}>{item.sku}</td>
                    <td className="px-3 py-2">
                      <span style={{ fontSize: 14, color: '#03045E' }}>{item.description}</span>
                      {!item.found && <span style={{ marginLeft: 8, color: '#854F0B', fontSize: 12, fontWeight: 700 }}>⚠ NOT IN PRICE LIST</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {editMode ? (
                        <input type="number" value={item.qty}
                          onChange={e => updateQty(item.id, e.target.value)}
                          onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                          onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                          style={{ width: 64, border: '1px solid #E3ECF5', borderRadius: 8, padding: '4px 6px', textAlign: 'center', fontSize: 14, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                      ) : (
                        <span style={{ fontWeight: 700, color: '#03045E' }}>{item.qty}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-xs" style={{ color: '#6B7C93' }}>{item.unit}</td>
                    <td className="px-3 py-2 text-right">
                      {editMode ? (
                        <input type="number" step="0.01" value={item.unitPrice}
                          onChange={e => updatePrice(item.id, e.target.value)}
                          onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                          onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                          style={{ width: 80, border: '1px solid #E3ECF5', borderRadius: 8, padding: '4px 6px', textAlign: 'right', fontSize: 14, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                      ) : (
                        <span style={{ color: '#6B7C93' }}>${parseFloat(item.unitPrice).toFixed(2)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: '#0077B6' }}>
                      ${round2(parseFloat(item.qty) * parseFloat(item.unitPrice)).toFixed(2)}
                    </td>
                    {editMode && (
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {/* One package-aware control per roundable row. When the
                              product carries no packQty the button stays VISIBLE
                              and disabled rather than disappearing — a missing
                              control looks like an item that needs no rounding,
                              which is precisely how barbed wire went unnoticed. */}
                          {item.roundable && (() => {
                            const packSize = getPackSize(item, products);
                            return packSize ? (
                              <button
                                onClick={() => roundRowToPackage(item)}
                                title={`Round up to a whole package (${packSize} per package)`}
                                style={{ background: '#E6F1FB', border: '1px solid #E3ECF5', color: '#0077B6', fontSize: 12, fontWeight: 700, padding: '2px 6px', borderRadius: 6, cursor: 'pointer' }}
                              >
                                ↑{packSize}
                              </button>
                            ) : (
                              <button
                                disabled
                                title={`Pack size not set in Inventory for ${item.sku} — add pcs per package to round this line`}
                                style={{ background: '#F1F4F8', border: '1px solid #E3ECF5', color: '#9AA8BC', fontSize: 12, fontWeight: 700, padding: '2px 6px', borderRadius: 6, cursor: 'not-allowed' }}
                              >
                                ↑ ?
                              </button>
                            );
                          })()}
                          <button onClick={() => removeItem(item.id)}
                            style={{ background: '#FBECEC', border: '1px solid #E9C3C3', color: '#A32D2D', fontSize: 12, fontWeight: 700, padding: '2px 6px', borderRadius: 6, cursor: 'pointer' }}>✕</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Subtotal */}
        <div className="flex justify-end mb-6">
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 16, width: 288 }}>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid #E3ECF5' }}>
              <span style={{ color: '#6B7C93', fontWeight: 600 }}>Materials Subtotal</span>
              <span style={{ color: '#0077B6', fontWeight: 800, fontSize: 18 }}>${subtotal.toFixed(2)}</span>
            </div>
            <p style={{ color: '#6B7C93', fontSize: 12, margin: '8px 0 0 0' }}>* Tax and delivery calculated in quote/invoice</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button onClick={() => navigate('/new-quote', { state: { lineItems: materials } })}
            style={{ background: '#0077B6', border: 'none', color: '#fff', fontWeight: 700, padding: '12px 32px', borderRadius: 12, fontSize: 16, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,119,182,0.3)' }}>
            📄 Create Quote
          </button>
          <button onClick={() => navigate('/new-invoice', { state: { lineItems: materials } })}
            style={{ background: '#0F6E56', border: 'none', color: '#fff', fontWeight: 700, padding: '12px 32px', borderRadius: 12, fontSize: 16, cursor: 'pointer', boxShadow: '0 4px 14px rgba(15,110,86,0.3)' }}>
            🧾 Create Invoice
          </button>
          <button onClick={() => navigate('/estimator')}
            style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', fontWeight: 600, padding: '12px 32px', borderRadius: 12, fontSize: 16, cursor: 'pointer' }}>
            ← New Estimate
          </button>
        </div>
      </div>
    </div>
  );
}

export default EstimatorResults;
